/**
 * Bereich B des Zielbilds vom 11.08.2026: FAST REIF.
 *
 *     MSFT · 3 von 4 Bedingungen erfüllt
 *     ✓ 12–40 % unter dem Hoch
 *     ✓ nicht näher als 5 % am Hoch
 *     ✓ Schwankung eingetrocknet
 *     ○ bestätigter Auslöser
 *     Zone öffnet über 105,00 · ungültig unter 90,00
 *     Nächste Prüfung: nach dem heutigen Tagesabschluss
 *
 * WOZU EIN EIGENER BEREICH
 * Zwischen „nichts zu tun" und „kaufen" lag bisher nichts. Wer wissen wollte,
 * ob überhaupt etwas heranreift, musste die Watchlist durchsehen. Diese Karte
 * beantwortet die Frage in einem Blick — und sie sagt AUSDRÜCKLICH, dass
 * nichts zu tun ist. Ein Bereich, der Erwartung weckt, ohne zur Handlung zu
 * drängen, ist genau die Lücke, die das Zielbild schließen wollte.
 *
 * KEINE ZWEITE AUSWAHL. Die Karte rechnet nichts. Sie liest die Tagesakte
 * (`v6_signal_snapshots`) und zeigt die Zeilen, in denen NUR der Auslöser
 * fehlt. Wäre die Auswahl weiter, entstünde eine zweite Watchlist.
 */
import { Link } from 'react-router-dom';
import { Hourglass } from 'lucide-react';

import { useSnapshots } from '@/hooks/use-snapshots';
import { istFastReifLautSnapshot, type SnapshotGelesen } from '@/lib/v6/snapshot-read';
import { CARD, CARD_HINT, EMPTY_STATE } from '@/lib/design-tokens';
import { useDemoMode } from '@/contexts/DemoModeContext';

/** Mehr als fünf sind keine Vorauswahl mehr. Der Rest wird gezählt, nicht gezeigt. */
const MAX_ZEILEN = 5;

export function FastReifCard() {
  const { bySymbol, standVom, veraltet, isLoading, leer } = useSnapshots();
  const { linkTo } = useDemoMode();

  const kandidaten = [...bySymbol.values()]
    .filter(istFastReifLautSnapshot)
    // Gleicher Fortschritt bei allen (nur der Auslöser fehlt), deshalb nach
    // Symbol — eine erfundene Rangfolge wäre schlimmer als gar keine.
    .sort((a, b) => a.symbol.localeCompare(b.symbol));

  const sichtbar = kandidaten.slice(0, MAX_ZEILEN);
  const weitere = kandidaten.length - sichtbar.length;

  return (
    <div className={CARD}>
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-sm font-semibold text-zinc-200 flex items-center gap-2">
          <Hourglass className="size-4 text-amber-400" />
          Fast reif
        </h2>
        <span className="text-[10px] uppercase tracking-wide text-zinc-500">
          {standVom ? `Stand ${standVom}` : 'kein Stand'}
        </span>
      </div>
      <p className={`${CARD_HINT} mb-3`}>
        Kandidaten, denen nur noch der bestätigte Auslöser fehlt.{' '}
        <span className="text-zinc-500">
          Hier ist nichts zu tun — die Liste sagt nur, ob überhaupt etwas
          heranreift.
        </span>
      </p>

      {veraltet && (
        <div className="mb-3 rounded border border-amber-800/50 bg-amber-950/20 px-2.5 py-2 text-[11px] text-amber-200 leading-snug">
          Der jüngste Eintrag stammt vom {standVom}. Solange der nächtliche Lauf
          nicht durchkommt, altert diese Liste — sie zeigt dann den Stand von
          damals, nicht den von heute.
        </div>
      )}

      {isLoading && <div className="skeleton h-16 w-full" />}

      {!isLoading && leer && (
        <div className={EMPTY_STATE}>
          Noch keine Tagesakte. Der nächtliche Lauf schreibt je Wert eine Zeile;
          bis dahin gibt es hier nichts zu zeigen — bewusst nichts Gerechnetes.
        </div>
      )}

      {!isLoading && !leer && kandidaten.length === 0 && (
        <div className={EMPTY_STATE}>
          Aktuell reift nichts heran. Das ist der Normalfall — bei rund 20
          Signalen im Jahr sind die meisten Tage leer.
        </div>
      )}

      {sichtbar.length > 0 && (
        <ul className="space-y-2">
          {sichtbar.map((row) => (
            <li key={row.symbol}>
              <Link
                to={linkTo(`/symbol/swing/${encodeURIComponent(row.symbol)}`)}
                className="block rounded border border-amber-900/40 bg-amber-950/10 px-2.5 py-2 hover:bg-amber-950/25 transition-colors focus-ring"
              >
                <KandidatZeile row={row} />
              </Link>
            </li>
          ))}
        </ul>
      )}

      {weitere > 0 && (
        <div className="mt-2 text-[10px] text-zinc-500">
          {weitere} weitere in der{' '}
          <Link to={linkTo('/watchlist')} className="text-blue-300 hover:text-blue-200 focus-ring rounded">
            Watchlist
          </Link>
          .
        </div>
      )}
    </div>
  );
}

function KandidatZeile({ row }: { row: SnapshotGelesen }) {
  const bedingungen = row.bedingungen ?? [];
  return (
    <>
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-mono text-sm font-semibold text-zinc-100">{row.symbol}</span>
        <span className="text-[10px] text-zinc-400">
          {row.bedingungen_erfuellt} von {row.bedingungen_gesamt} Bedingungen erfüllt
        </span>
      </div>

      {bedingungen.length > 0 && (
        <ul className="mt-1 space-y-0.5">
          {bedingungen.map((b) => (
            <li
              key={b.id}
              className={
                b.erfuellt === true
                  ? 'text-[11px] text-emerald-300/90 leading-snug'
                  : 'text-[11px] text-zinc-400 leading-snug'
              }
            >
              <span aria-hidden className="inline-block w-3">
                {b.erfuellt === true ? '✓' : b.erfuellt === null ? '–' : '○'}
              </span>
              {b.text}
              {b.erfuellt === null && <span className="text-zinc-600"> (nicht bewertbar)</span>}
            </li>
          ))}
        </ul>
      )}

      {(row.trigger_ab !== null || row.initial_stop !== null) && (
        <div className="mt-1 text-[11px] font-mono tabular-nums text-zinc-300">
          {row.trigger_ab !== null && <>Zone öffnet über {row.trigger_ab.toFixed(2)}</>}
          {row.trigger_ab !== null && row.initial_stop !== null && (
            <span className="text-zinc-600"> · </span>
          )}
          {row.initial_stop !== null && (
            <>
              <span className="text-zinc-500">ungültig unter </span>
              {row.initial_stop.toFixed(2)}
            </>
          )}
        </div>
      )}

      {row.naechste_pruefung && (
        <div className="mt-0.5 text-[10px] text-zinc-500 leading-snug">
          {row.naechste_pruefung}
        </div>
      )}
    </>
  );
}
